import { Box, Grid, ListItemText } from '@material-ui/core';
import { images } from 'assets';
import { StyledChip, AnimatedCounter, BasicThreeDotsMenu } from 'components/shared';
import { theme } from 'config/theme';
import i18n from 'i18n-js';
import dayjs from 'dayjs';
import { useAppSelector } from 'index';
import React, { useEffect, useRef, useState } from 'react';
import { useFirstLoad } from 'hooks/useFirstLoad';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { FinanceReportSkeleton } from '../FinanceSkeletons';
import financeStore from 'store/financeStore';
import { actionCreators } from 'store/uiStore/actionCreators';
import { DateHistoryData, DateFilterType } from 'store/uiStore/interfaces';
import KPICard from '../../shared/kpiCard';
import { printElement } from '../../shared/printHelper';
import styles from '../../styles';
import DateFilters from '../DateFIlters';
import { ChartDataItem, ChartBarCheckbox, Charts } from '../finances/components/Charts';
import PaymentReportTable from '../payments/components/paymentsTable';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { formatDateForServer } from 'helpers/helperFunctions';
import { PaymentType } from 'constants/enums';

interface Props {
    groupId: string;
}

export default function GroupPayments(props: Props) {
    const { groupId } = props;
    const classes = styles();
    const dispatch = useThunkDispatch();
    const [isReportLoading, settleReport] = useFirstLoad();
    const report = useAppSelector((state) => state.finance.groupPayments);
    const currency = useAppSelector(
        (state) => state.restaurantDetails?.restaurantInformation?.paymentDetails?.currency || 'CHF'
    );
    const { dateFilter, dateHistory, dateFrom, dateTo } = useAppSelector(
        (state) => state.ui.finance
    );
    const [chartView, setChartView] = useState(false);
    const [hoveredSwitchIcon, setHoveredSwitchIcon] = useState(0);
    const paymentMethods = Array.from(
        new Set(
            report.transactions.map((x) =>
                x.type === PaymentType.Refund ? PaymentType.Refund : x.method
            )
        )
    );
    const dataList: ChartDataItem[] = (report.transactions || []).map((x) => {
        const sign = x.type === PaymentType.Refund ? -1 : 1;
        return {
            name: x.type === PaymentType.Refund ? PaymentType.Refund : x.method,
            amount: (x.amount + x.tipAmount) * sign,
            date: x.paymentDate,
        };
    });
    const containerRef = useRef<HTMLDivElement>(null);
    const previousGroupIdRef = useRef<string | null>(null);
    const totalPaymentsCount = report.payments.reduce(
        (acc, next) => (acc += next.transactionCount),
        0
    );
    const totalRefundsAmount = report.refunds.reduce(
        (acc, next) => (acc += next.amount + next.tipsAmount),
        0
    );

    useEffect(() => {
        if (previousGroupIdRef.current === null) {
            previousGroupIdRef.current = groupId;
            return;
        }

        if (previousGroupIdRef.current !== groupId) {
            getData(0, dateHistory, dateFrom ?? undefined, dateTo ?? undefined);
            previousGroupIdRef.current = groupId;
        }
    }, [groupId]);

    function getData(
        dayAgo: number,
        dateHistoryList?: DateHistoryData[] | undefined,
        dateFrom?: Date | undefined,
        dateTo?: Date | undefined
    ): void {
        var from = dateFrom ?? dayjs().subtract(dayAgo, 'day').toDate();
        var to = dateTo ?? dayjs().toDate();

        dateHistoryList && setSelectedDatesHistory(dateHistoryList);

        dispatch(
            financeStore.actionCreators.getGroupPaymentReport(
                groupId,
                formatDateForServer(from),
                formatDateForServer(to)
            )
        ).finally(settleReport);
    }

    function getIconSource(i: number) {
        if (hoveredSwitchIcon > 0) {
            if (i === 0 && hoveredSwitchIcon === 1) {
                return images.icons.listSelected;
            }
            if (i === 1 && hoveredSwitchIcon === 2) {
                return images.icons.graphSelected;
            }
        }

        switch (chartView) {
            case true:
                switch (i) {
                    case 0:
                        return images.icons.list;
                    case 1:
                        return images.icons.graphSelected;
                }
                break;
            default: {
                switch (i) {
                    case 0:
                        return images.icons.listSelected;
                    case 1:
                        return images.icons.graph;
                }
            }
        }
    }

    function handleDateRangeChange(from: Date | null, to: Date | null): void {
        dispatch(actionCreators.setFinanceDateRange(from, to));
    }

    function exportData(): void {
        var from = formatDateForServer(dateFrom ?? dayjs().subtract(1, 'day').toDate());
        var to = formatDateForServer(dateTo ?? dayjs().toDate());

        dispatch(financeStore.actionCreators.getGroupPaymentReportExcel(groupId, from, to));
    }

    function getCheckboxList(): ChartBarCheckbox[] {
        return paymentMethods.map<ChartBarCheckbox>((method) => {
            return {
                id: method,
                name: i18n.t(`finances.paymentMethodEnum.${method.toLowerCase()}`),
                label: <>{i18n.t(`finances.paymentMethodEnum.${method.toLowerCase()}`)}</>,
            };
        });
    }

    function handlePrint(): void {
        printElement(containerRef);
    }

    function setDateFilter(filter: DateFilterType) {
        dispatch(actionCreators.setFinanceDateFilter(filter));
    }

    function setSelectedDatesHistory(dateHistory: DateHistoryData[]) {
        dispatch(actionCreators.setFinanceDateHistory(dateHistory));
    }

    return (
        <div
            ref={containerRef}
            className={classes.pBottom5}
            style={{ position: 'relative', height: '100%', overflow: 'auto' }}
        >
            <div className={classes.topContainer} style={{ justifyContent: 'space-between' }}>
                <StyledChip
                    icon={<InfoOutlinedIcon />}
                    label={i18n.t('finances.filteredByPaymentConfirmationDate')}
                    size="small"
                    labelFontSize={12}
                    labelColor={theme.palette.primary.dark}
                    variant="outlined"
                    color="primary"
                />
            </div>

            <div className={classes.kpiContainer}>
                <Box mb={3}>
                    <Grid container spacing={2}>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.netPayments')}
                                value={
                                    <AnimatedCounter
                                        value={report.totalAmount - totalRefundsAmount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={`${currency} ${report.totalAmount.toFixed(2)} ${i18n
                                    .t('finances.received')
                                    .toLowerCase()} \n${currency} ${totalRefundsAmount.toFixed(
                                    2
                                )} ${i18n.t('finances.refunded').toLowerCase()}`}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.cash')}
                                value={
                                    <AnimatedCounter
                                        value={report.byCash.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={`${report.byCash.paymentsCount} ${i18n
                                    .t('finances.payments')
                                    .toLowerCase()}, ${report.byCash.refundsCount} ${i18n
                                    .t('finances.refunds')
                                    .toLowerCase()}`}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.cardEft')}
                                value={
                                    <AnimatedCounter
                                        value={report.byCard.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={`${report.byCard.paymentsCount} ${i18n
                                    .t('finances.payments')
                                    .toLowerCase()}, ${report.byCard.refundsCount} ${i18n
                                    .t('finances.refunds')
                                    .toLowerCase()}`}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.debtor')}
                                value={
                                    <AnimatedCounter
                                        value={report.byDebtor.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={`${report.byDebtor.paymentsCount} ${i18n
                                    .t('finances.payments')
                                    .toLowerCase()}, ${report.byDebtor.refundsCount} ${i18n
                                    .t('finances.refunds')
                                    .toLowerCase()}`}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.twint')}
                                value={
                                    <AnimatedCounter
                                        value={report.byTwint.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={`${report.byTwint.paymentsCount} ${i18n
                                    .t('finances.payments')
                                    .toLowerCase()}, ${report.byTwint.refundsCount} ${i18n
                                    .t('finances.refunds')
                                    .toLowerCase()}`}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.tips')}
                                value={
                                    <AnimatedCounter
                                        value={report.totalTipsAmount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={i18n.t('finances.includedInTotal')}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </div>

            <div className={classes.dateContainer}>
                <div className={classes.flexRow}>
                    <DateFilters
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        dateFilter={dateFilter}
                        setDateFilter={setDateFilter}
                        getData={getData}
                        selectedDatesHistory={dateHistory}
                        setSelectedDatesHistory={setSelectedDatesHistory}
                        isChartsShowing={chartView}
                        onDateRangeChange={handleDateRangeChange}
                        showClosing={false}
                    />
                    <span style={{ marginLeft: 20 }}></span>
                    <div className={classes.switchGraphContainer}>
                        <img
                            onClick={() => setChartView(false)}
                            onMouseOver={() => setHoveredSwitchIcon(1)}
                            onMouseLeave={() => setHoveredSwitchIcon(0)}
                            className={classes.switchBasic}
                            src={getIconSource(0)}
                            alt="switch-graph"
                        />
                        <img
                            onClick={() => {
                                setChartView(true);
                            }}
                            onMouseOver={() => setHoveredSwitchIcon(2)}
                            onMouseLeave={() => setHoveredSwitchIcon(0)}
                            className={classes.switchBasic}
                            src={getIconSource(1)}
                            alt="switch-gaph"
                        />
                    </div>
                    <BasicThreeDotsMenu
                        id="hide-in-print"
                        items={[
                            <div
                                id="hide-in-print"
                                className={classes.threeDotsMenuItemContainer}
                                onClick={handlePrint}
                            >
                                <img
                                    src={images.icons.print}
                                    alt="print-icon"
                                    className={classes.threeDotsItemIcon}
                                />
                                <ListItemText primary={i18n.t('common.print')} />
                            </div>,
                            <div
                                id="hide-in-print"
                                className={classes.threeDotsMenuItemContainer}
                                onClick={exportData}
                            >
                                <img
                                    src={images.icons.fileDownload}
                                    alt="file-icon"
                                    className={classes.threeDotsItemIcon}
                                />
                                <ListItemText primary={i18n.t('common.excelExport')} />
                            </div>,
                        ]}
                    />
                </div>
            </div>

            {chartView && (
                <Charts
                    currency={currency}
                    selectedDatesHistory={dateHistory}
                    dateFilter={dateFilter}
                    dataList={dataList}
                    checkboxList={getCheckboxList()}
                    onDateClick={(selectedDates, newDateFilter, dateHistoryList) => {
                        // const { startDate, endDate } = selectedDates;
                        setSelectedDatesHistory(dateHistoryList);
                        // return dispatch(
                        //     financeStore.actionCreators.getPaymentReport(
                        //         formatDateForServer(startDate),
                        //         formatDateForServer(endDate),
                        //         restaurantId
                        //     )
                        // );
                    }}
                />
            )}
            {!chartView && (
                <PaymentReportTable
                    payments={report.payments}
                    refunds={report.refunds}
                    currency={currency}
                    totalPaymentsCount={totalPaymentsCount}
                    totalAmount={report.totalAmount}
                    totalTips={report.totalTipsAmount}
                />
            )}
            <FinanceReportSkeleton isLoading={isReportLoading} kpiCount={6} />
        </div>
    );
}
